'use client'

import { useState, forwardRef } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { Mic, Calendar, Hash, MoreVertical, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FollowButton } from '@/components/podcast/FollowButton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface PodcastCardProps {
  podcast: {
    id: string
    title: string
    author?: string
    description?: string
    imageUrl?: string
    totalEpisodes?: number
    lastEpisodeDate?: string
    categories?: string[]
  }
  userId?: string
  onUnfollow?: () => void
}

export const PodcastCard = forwardRef<HTMLDivElement, PodcastCardProps>(
  ({ podcast, userId, onUnfollow }, ref) => {
    const [imageError, setImageError] = useState(false)
    const podcastUrl = `/podcast/${podcast.id}`

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No episodes'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        className="group relative"
      >
      <div className="flex gap-4 p-4 rounded-xl border border-border/50 hover:border-primary/30 transition-all duration-200 hover:bg-primary/5">
        {/* Podcast Cover */}
        <Link href={podcastUrl} className="flex-shrink-0">
          <div className="w-24 h-24 rounded-lg overflow-hidden bg-gradient-to-br from-primary/20 to-primary/10 relative">
            {podcast.imageUrl && !imageError ? (
              <Image
                src={podcast.imageUrl}
                alt={podcast.title}
                fill
                className="object-cover group-hover:scale-110 transition-transform duration-300"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Mic className="w-8 h-8 text-primary/40" />
              </div>
            )}
          </div>
        </Link>

        {/* Podcast Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <Link href={podcastUrl} className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg line-clamp-1 group-hover:text-primary transition-colors">
                {podcast.title}
              </h3>
            </Link>

            {/* Actions */}
            <div className="flex items-center gap-1 ml-2">
              <FollowButton
                podcastId={podcast.id}
                userId={userId}
                podcastSnapshot={{
                  id: podcast.id,
                  title: podcast.title,
                  author: podcast.author,
                  imageUrl: podcast.imageUrl,
                  feedUrl: (podcast as any).feedUrl,
                  defaultLevelRange: (podcast as any).defaultLevelRange
                }}
                size="sm"
                variant="ghost"
              />
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link href={podcastUrl} className="flex items-center">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      View Podcast
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={onUnfollow}
                    className="text-destructive focus:text-destructive"
                  >
                    Unfollow
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Author */}
          {podcast.author && (
            <p className="text-sm text-muted-foreground mb-2 line-clamp-1">
              by {podcast.author}
            </p>
          )}

          {/* Description */}
          {podcast.description && (
            <p className="text-sm text-muted-foreground/80 line-clamp-2 mb-3">
              {podcast.description}
            </p>
          )}

          {/* Metadata */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            {/* Episodes count */}
            {podcast.totalEpisodes && podcast.totalEpisodes > 0 && (
              <div className="flex items-center gap-1">
                <Hash className="w-3 h-3" />
                <span>{podcast.totalEpisodes} episodes</span>
              </div>
            )}

            {/* Last episode date */}
            {podcast.lastEpisodeDate && (
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>Latest: {formatDate(podcast.lastEpisodeDate)}</span>
              </div>
            )}

            {/* Categories */}
            {podcast.categories && podcast.categories.length > 0 && (
              <div className="flex items-center gap-1">
                <span className="px-2 py-0.5 bg-primary/10 rounded-full">
                  {podcast.categories[0]}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
    )
  }
)

PodcastCard.displayName = 'PodcastCard'
